/**
 * Feature entitlement catalog — the single source of truth for authorization
 * LAYER 2. Verbatim transcription of docs/ARCHITECTURE.md §5 (26 features).
 *
 * `defaultState` is the state a BRAND-NEW tenant is provisioned with:
 * 20 vendor-backed features `DISABLED`, 3 `NOT_ENTITLED` (the upstream account
 * answers 403 for those groups — they are modelled, not omitted, so they light
 * up without a schema change if entitlement is granted later), and the 3
 * platform features `ENABLED`.
 *
 * `requireFeature` treats `DISABLED` and `NOT_ENTITLED` identically at the API
 * boundary (both → 403 FORBIDDEN_FEATURE) so a client cannot distinguish
 * "you did not buy it" from "the upstream account cannot reach it". Only
 * `admin.features.read` sees the difference.
 *
 * `vendorGroups` holds the upstream product-group tag names purely as
 * documentation for the superadmin console. No URL, host or header name
 * belonging to the upstream vendor appears in this package.
 */
export const FEATURES = {
    'account.overview': {
        key: 'account.overview',
        label: 'Account overview',
        description: 'Account profile, estate statistics and the plan catalog.',
        vendorGroups: ['Customer / Account Details'],
        permissions: ['account.read', 'plans.read'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 1,
    },
    'inventory.baskets': {
        key: 'inventory.baskets',
        label: 'SIM inventory and baskets',
        description: 'Browse baskets and the SIMs inside them.',
        vendorGroups: ['SIM Inventory & Baskets'],
        permissions: ['baskets.read', 'sims.read'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 2,
    },
    'inventory.export': {
        key: 'inventory.export',
        label: 'Inventory export',
        description: 'Request inventory exports, browse export history and download files.',
        vendorGroups: ['SIM Inventory & Baskets'],
        permissions: ['sims.export'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 3,
    },
    'sims.lifecycle.activate': {
        key: 'sims.lifecycle.activate',
        label: 'Activate SIM',
        description: 'Activate SIMs with a plan and activate SIMs out of test mode.',
        vendorGroups: ['Activate SIM'],
        permissions: ['sims.activate'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 4,
    },
    'sims.lifecycle.test_mode': {
        key: 'sims.lifecycle.test_mode',
        label: 'Activate SIM in test mode',
        description: 'Activate SIMs in test mode.',
        vendorGroups: ['Activate SIM (Test Mode)'],
        permissions: ['sims.activate_test'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 5,
    },
    'sims.lifecycle.swap': {
        key: 'sims.lifecycle.swap',
        label: 'Swap SIM',
        description: 'Swap an existing SIM for a new one.',
        vendorGroups: ['Swap SIM'],
        permissions: ['sims.swap'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 6,
    },
    'sims.lifecycle.safe_custody': {
        key: 'sims.lifecycle.safe_custody',
        label: 'Safe custody',
        description: 'Place SIMs into safe custody and take them out again.',
        vendorGroups: ['Safe Custody', 'Out of Safe Custody'],
        permissions: ['sims.suspend', 'sims.resume'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 7,
    },
    'sims.lifecycle.disconnect': {
        key: 'sims.lifecycle.disconnect',
        label: 'Temporary disconnection',
        description: 'Temporarily disconnect SIMs and resume them.',
        vendorGroups: ['Disconnection'],
        permissions: ['sims.disconnect'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 8,
    },
    'sims.plan_upgrade': {
        key: 'sims.plan_upgrade',
        label: 'Plan upgrade',
        description: 'List upgradable SIMs and change their plan.',
        vendorGroups: ['Plan Upgrade'],
        permissions: ['sims.change_plan'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 9,
    },
    'sims.usage': {
        key: 'sims.usage',
        label: 'Data usage',
        description: 'Real-time data usage per SIM.',
        vendorGroups: ['Data Usage'],
        permissions: ['sims.usage.read'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 10,
    },
    'jobs.tracking': {
        key: 'jobs.tracking',
        label: 'Job tracking',
        description: 'Track asynchronous operations and their per-line-item outcomes.',
        vendorGroups: ['Jobs', 'Orders'],
        permissions: ['jobs.read'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 11,
    },
    'kyc.bulk': {
        key: 'kyc.bulk',
        label: 'Bulk KYC',
        description: 'Upload bulk KYC files and download the KYC template.',
        vendorGroups: ['KYC Bulk Orders'],
        permissions: ['kyc.bulk.write'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 12,
    },
    'messaging.centre': {
        key: 'messaging.centre',
        label: 'Messaging centre',
        description: 'SMS quota, billing history, templates, sending and message history.',
        vendorGroups: ['Messaging Centre'],
        permissions: [
            'sms.quota.read',
            'sms.billing.read',
            'sms.templates.read',
            'sms.templates.write',
            'sms.send',
            'sms.send_bulk',
            'sms.history.read',
        ],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 13,
    },
    'whitelist.sms': {
        key: 'whitelist.sms',
        label: 'SMS whitelisting',
        description: 'Fetch and batch-update whitelisted SMS numbers.',
        vendorGroups: ['SMS Whitelisting'],
        permissions: ['whitelist.sms.read', 'whitelist.sms.write'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 14,
    },
    'whitelist.voice': {
        key: 'whitelist.voice',
        label: 'Voice whitelisting',
        description: 'Fetch and update whitelisted voice numbers.',
        vendorGroups: ['Voice Whitelisting'],
        permissions: ['whitelist.voice.read', 'whitelist.voice.write'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 15,
    },
    'whitelist.country': {
        key: 'whitelist.country',
        label: 'Country whitelisting',
        description: 'Country catalog, whitelisted countries and whitelist request status.',
        vendorGroups: ['Country Whitelisting'],
        permissions: ['whitelist.country.read', 'whitelist.country.write'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 16,
    },
    'location.centre': {
        key: 'location.centre',
        label: 'Location centre',
        description: 'Location whitelisting requests and address lookup by MSISDN.',
        vendorGroups: ['Location Centre'],
        permissions: ['location.read', 'location.request'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 17,
    },
    'diagnostics.radius': {
        key: 'diagnostics.radius',
        label: 'Device diagnostics',
        description: 'AAA / RADIUS session diagnostics for a device.',
        vendorGroups: ['AAA / Device Diagnostics'],
        permissions: ['diagnostics.read'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 18,
    },
    'arp.management': {
        key: 'arp.management',
        label: 'Advanced Rental Plan',
        description: 'ARP SIM list, next plans, per-SIM and account-default expiration actions.',
        vendorGroups: ['Advanced Rental Plan (ARP)'],
        permissions: ['arp.read', 'arp.write'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 19,
    },
    'arp.bulk': {
        key: 'arp.bulk',
        label: 'ARP bulk operations',
        description: 'ARP bulk upload and bulk export.',
        vendorGroups: ['ARP Bulk'],
        permissions: ['arp.bulk.write'],
        defaultState: 'DISABLED',
        isVendorBacked: true,
        sortOrder: 20,
    },
    // ── The three groups the upstream account answers 403 for (§5, §13.13) ────
    // Modelled, not omitted. No routes exist for them and none may be invented:
    // their upstream paths are not in the reference specification.
    'orders.bulk_generic': {
        key: 'orders.bulk_generic',
        label: 'Bulk orders (generic)',
        description: 'Not entitled on the current upstream account; the operation paths are not published, so no routes exist.',
        vendorGroups: ['Bulk Orders (generic)'],
        permissions: ['orders.bulk.write'],
        defaultState: 'NOT_ENTITLED',
        isVendorBacked: true,
        sortOrder: 21,
    },
    'whitelist.data': {
        key: 'whitelist.data',
        label: 'Data whitelisting',
        description: 'Not entitled on the current upstream account; the operation paths are not published, so no routes exist.',
        vendorGroups: ['Data Whitelisting'],
        permissions: ['whitelist.data.read', 'whitelist.data.write'],
        defaultState: 'NOT_ENTITLED',
        isVendorBacked: true,
        sortOrder: 22,
    },
    'kyc.details': {
        key: 'kyc.details',
        label: 'KYC details',
        description: 'Not entitled on the current upstream account; the operation paths are not published, so no routes exist.',
        vendorGroups: ['KYC Details'],
        permissions: ['kyc.details.read'],
        defaultState: 'NOT_ENTITLED',
        isVendorBacked: true,
        sortOrder: 23,
    },
    // ── Ours, no upstream call ───────────────────────────────────────────────
    'platform.user_management': {
        key: 'platform.user_management',
        label: 'User management',
        description: 'Invite and manage users and custom roles inside the tenant.',
        vendorGroups: [],
        permissions: ['users.read', 'users.write', 'roles.read'],
        defaultState: 'ENABLED',
        isVendorBacked: false,
        sortOrder: 24,
    },
    'platform.audit_log': {
        key: 'platform.audit_log',
        label: 'Audit log',
        description: "Read the tenant's own audit trail.",
        vendorGroups: [],
        permissions: ['audit.read'],
        defaultState: 'ENABLED',
        isVendorBacked: false,
        sortOrder: 25,
    },
    'platform.webhooks': {
        key: 'platform.webhooks',
        label: 'Webhooks',
        description: 'Subscribe to job-completion events delivered to customer endpoints.',
        vendorGroups: [],
        permissions: ['webhooks.manage'],
        defaultState: 'ENABLED',
        isVendorBacked: false,
        sortOrder: 26,
    },
};
/** All 26 feature keys in `sortOrder` order. */
export const FEATURE_KEYS = Object.keys(FEATURES);
/** The 20 upstream-backed feature keys that are reachable today. */
export const VENDOR_BACKED_FEATURE_KEYS = FEATURE_KEYS.filter((k) => FEATURES[k].isVendorBacked && FEATURES[k].defaultState !== 'NOT_ENTITLED');
/** The 3 feature keys the upstream account cannot reach (§5 rows 21–23). */
export const NOT_ENTITLED_FEATURE_KEYS = FEATURE_KEYS.filter((k) => FEATURES[k].defaultState === 'NOT_ENTITLED');
/** The 3 features implemented entirely by us. */
export const PLATFORM_FEATURE_KEYS = FEATURE_KEYS.filter((k) => !FEATURES[k].isVendorBacked);
export function isFeatureKey(value) {
    return Object.prototype.hasOwnProperty.call(FEATURES, value);
}
//# sourceMappingURL=features.js.map